class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // Replace the FULL drawer inner content so the header item-count,
          // the "Your cart is empty" warning, cart items, and footer are all
          // kept in sync — previously only cart-drawer-items and the footer
          // were swapped, leaving the header stuck on a stale count.
          const targetInner = document.querySelector('#CartDrawer .drawer__inner');
          const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
          if (targetInner && sourceInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
          }

          // Sync the is-empty class on the <cart-drawer> custom element.
          // The CSS uses cart-drawer.is-empty to hide/show the header and
          // the "Your cart is empty" warnings, so this MUST stay correct.
          const cartDrawerEl = document.querySelector('cart-drawer');
          const sourceCartDrawer = html.querySelector('cart-drawer');
          if (cartDrawerEl && sourceCartDrawer) {
            cartDrawerEl.classList.toggle(
              'is-empty',
              sourceCartDrawer.classList.contains('is-empty')
            );
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }

    // Re-sync product cards for cart updates originating from other sources
    // (e.g. the same product added/removed elsewhere on the page).
    syncProductCardCartState();
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    // Rapid clicks on +/- each trigger their own debounced fetch to
    // /cart/change.js. Network responses can arrive out of order (a later
    // click's request can resolve before an earlier click's request), and
    // without guarding against that, whichever response finishes LAST wins
    // and overwrites the DOM — even if it was actually an earlier, now-stale
    // request. That's what produces the quantity visibly jumping backwards
    // mid-click (e.g. 1 → 2 → 3 → 2 → 3 → 4). Tag every request with a
    // per-line sequence number and ignore any response that isn't the most
    // recent one issued for that line.
    this._qtyRequestSeq = this._qtyRequestSeq || {};
    const requestSeq = (this._qtyRequestSeq[line] = (this._qtyRequestSeq[line] || 0) + 1);
    const isStale = () => this._qtyRequestSeq[line] !== requestSeq;

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        if (isStale()) return;
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);
        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        // Keep homepage / collection product cards in sync so the "Add to Cart"
        // button reappears when an item is removed (or its quantity changed)
        // from inside the cart drawer or cart page.
        syncProductCardCartState(parsedState);
      })
      .catch(() => {
        if (isStale()) return;
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (isStale()) return;
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
/**
 * Reconciles the "Add to Cart" / quantity-stepper state of every product card
 * (`.quantity-add-to-cart-btns`) on the page with the current cart contents.
 *
 * A card shows the quantity stepper (instead of the "Add to Cart" button) only
 * while it has the `added-item` class. Previously nothing removed that class
 * when an item was removed from inside the cart drawer or cart page, leaving
 * the card stuck on the stepper with a stale quantity. Calling this after any
 * cart change keeps each card correct: present in cart -> stepper with the
 * right quantity; absent from cart -> "Add to Cart" button restored.
 *
 * @param {Object} [cart] Optional cart object (e.g. the parsed response from
 *   /cart/change.js). If omitted, the live cart is fetched from /cart.js.
 */
function syncProductCardCartState(cart) {
  const reconcile = (cartData) => {
    if (!cartData || !Array.isArray(cartData.items)) return;

    document.querySelectorAll('.quantity-add-to-cart-btns[data-variant-id]').forEach((card) => {
      const variantId = parseInt(card.dataset.variantId, 10);
      if (!variantId) return;

      const cartItem = cartData.items.find((item) => item.id === variantId);
      const quantityInput = card.querySelector('.card-quantity-input, .custom-quantity-input');

      if (cartItem) {
        // Item is in the cart: show the stepper, mirror the cart quantity.
        card.classList.add('added-item');
        if (quantityInput) quantityInput.value = cartItem.quantity;
      } else {
        // Item is no longer in the cart: restore the "Add to Cart" button
        // and reset the stepper to its minimum (MOQ when set, otherwise 1).
        card.classList.remove('added-item');
        if (quantityInput) {
          const minValue = parseInt(quantityInput.dataset.min || quantityInput.getAttribute('min'), 10);
          quantityInput.value = minValue > 0 ? minValue : 1;
        }
      }
    });
  };

  if (cart && Array.isArray(cart.items)) {
    reconcile(cart);
  } else {
    fetch('/cart.js')
      .then((response) => response.json())
      .then(reconcile)
      .catch((e) => console.error('Failed to sync product card cart state:', e));
  }
}

window.syncProductCardCartState = syncProductCardCartState;

document.addEventListener('DOMContentLoaded', async function () {
  try {
    const cart = await fetch('/cart.js').then(res => res.json());
    cart.items.forEach(item => {
      const variantId = item.id;
      const forms = document.querySelectorAll('form');

      forms.forEach(form => {
        const formVariantId = parseInt(form.querySelector('input[name="id"]')?.value, 10);
        if (formVariantId === variantId) {
          let container = form.closest('.quantity-add-to-cart-btns') ||
                          form.querySelector('.quantity-add-to-cart-btns') ||
                          form.parentElement?.querySelector('.quantity-add-to-cart-btns');
          if (container) container.classList.add('added-item');

          const input = form.querySelector('.custom-quantity-input');
          if (input) input.value = item.quantity;
        }
      });
    });
  } catch (err) {
    console.error('Error checking cart items on page load:', err);
  }
});